import {
  AGENT_RUN_STATUSES,
  AGENT_RUNTIMES,
  type AgentRunListItem,
  type AgentRunStatus,
  type AgentRuntime,
  type AgentsOverview,
} from '@fos/shared/contracts/agents';
import { AGENT_RUN_STATUS_LABELS, AGENT_RUNTIME_LABELS, AGENTS_SAFETY_NOTE } from '@fos/shared/labels';
import { Dec } from '@fos/shared/money';
import { Activity, Bot, Info, Loader, Search, Settings, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { formatMoneyShort, plural } from '../../../lib/format';
import { useScope } from '../../../lib/scope';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  ListSkeleton,
  Page,
  PageHeader,
  Section,
  SectionHeader,
  Select,
  Skeleton,
  Stat,
  TabPanel,
  Tabs,
} from '../../../ui';
import { AiHistoryView } from '../../history/AiHistoryView';
import { TOUCH_BUTTON } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { RunnersSection } from '../components/Runners';
import { RunsList } from '../components/RunsList';
import { runPath } from '../format';

/**
 * Agenci (Figma 11): kafelki „Budżet AI”, „Czeka na odbiór”, „Przebiegi w tym miesiącu”, „Ostatnie przebiegi”
 * (szukanie po problemie, filtry statusu i wykonawcy), nota bezpieczeństwa. Zakładka „Komputery” — runnery.
 * Zakres listy: przełącznik „Moje / Zespół” z menu bocznego.
 */

const MONTHS_LOCATIVE = [
  'styczniu',
  'lutym',
  'marcu',
  'kwietniu',
  'maju',
  'czerwcu',
  'lipcu',
  'sierpniu',
  'wrześniu',
  'październiku',
  'listopadzie',
  'grudniu',
];

type StatusFilter = 'all' | 'to_review' | AgentRunStatus;
type RuntimeFilter = 'all' | AgentRuntime;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Wszystkie statusy' },
  { value: 'to_review', label: 'Do odbioru' },
  ...AGENT_RUN_STATUSES.map((s) => ({ value: s, label: AGENT_RUN_STATUS_LABELS[s] })),
];

const RUNTIME_OPTIONS: { value: RuntimeFilter; label: string }[] = [
  { value: 'all', label: 'Każdy wykonawca' },
  ...AGENT_RUNTIMES.map((r) => ({ value: r, label: AGENT_RUNTIME_LABELS[r] })),
];

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function AgentsPage() {
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const widok = params.get('widok');
  // Fala 10: „Historia AI” — działania AI z poleceniem, które je wywołało (moduł history)
  const tab = widok === 'komputery' ? 'runners' : widok === 'historia-ai' ? 'ai-history' : 'runs';
  if (!access.can('agents')) return <ModulePage module="agents" />;
  return (
    <Page>
      <PageHeader
        title="Agenci"
        meta="Co AI robi i robiło dla Ciebie. Każdy przebieg ma log, wynik i koszt."
        actions={
          access.isManagerOrAbove ? (
            <Button asChild variant="secondary" icon={Settings} className={TOUCH_BUTTON}>
              <Link to="/ustawienia/ai">Ustawienia AI</Link>
            </Button>
          ) : undefined
        }
      />
      <Tabs
        className="mt-6"
        aria-label="Widok Agentów"
        value={tab}
        onValueChange={(v) =>
          setParams(v === 'runners' ? { widok: 'komputery' } : v === 'ai-history' ? { widok: 'historia-ai' } : {}, {
            replace: true,
          })
        }
        items={[
          { value: 'runs', label: 'Przebiegi' },
          { value: 'runners', label: 'Komputery' },
          { value: 'ai-history', label: 'Historia AI' },
        ]}
      >
        <TabPanel value="runs">
          <RunsView />
        </TabPanel>
        <TabPanel value="runners">
          <div className="mt-6">
            <RunnersSection />
          </div>
        </TabPanel>
        <TabPanel value="ai-history">
          <AiHistoryView />
        </TabPanel>
      </Tabs>
    </Page>
  );
}

function OverviewTiles({ data }: { data: AgentsOverview }) {
  const month = MONTHS_LOCATIVE[Number(data.month.slice(5, 7)) - 1] ?? '';
  const budget = data.budget;
  let budgetTile = null;
  if (budget) {
    const spent = new Dec(budget.spentPln);
    const limit = budget.limitPln ? new Dec(budget.limitPln) : null;
    const left = limit ? Dec.max(limit.minus(spent), 0) : null;
    budgetTile = (
      <Stat
        className="col-span-2 md:col-span-1"
        label={`${budget.scope === 'workspace' ? 'Budżet AI' : 'Twój limit AI'} w ${month}`}
        icon={Wallet}
        value={
          limit
            ? `${formatMoneyShort(spent.toFixed(2)).replace(/\s?zł$/, '')} / ${formatMoneyShort(limit.toFixed(2))}`
            : formatMoneyShort(spent.toFixed(2))
        }
        progress={limit?.gt(0) ? Math.min(1, spent.div(limit).toNumber()) : undefined}
        tone={limit && spent.gte(limit) ? 'danger' : 'accent'}
        showDot={false}
        note={left ? `zostało ${formatMoneyShort(left.toFixed(2))}` : 'bez limitu'}
      />
    );
  }
  const first = data.toReview.first;
  const r = data.runs;
  const notOk = r.failed + r.cancelled;
  return (
    <div className={`grid grid-cols-2 gap-3 ${budgetTile ? 'md:grid-cols-3' : ''}`}>
      {budgetTile}
      <Stat
        label="Czeka na odbiór"
        icon={Loader}
        value={data.toReview.count}
        tone="accent"
        showDot={Boolean(first)}
        note={first ? first.label : 'nic nie czeka'}
        to={first ? runPath(first.id) : undefined}
      />
      <Stat
        label="Przebiegi w tym miesiącu"
        icon={Activity}
        value={r.total}
        showDot={false}
        note={
          r.total === 0
            ? 'jeszcze żadnego'
            : [
                `${r.succeeded} ${plural(r.succeeded, ['udany', 'udane', 'udanych'])}`,
                notOk > 0 ? `${notOk} ${plural(notOk, ['przerwany', 'przerwane', 'przerwanych'])}` : null,
                r.open > 0 ? `${r.open} w toku` : null,
              ]
                .filter(Boolean)
                .join(', ')
        }
      />
    </div>
  );
}

function RunsView() {
  const access = useAccess();
  const { scope } = useScope();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [runtime, setRuntime] = useState<RuntimeFilter>('all');
  const query = useDebounced(q.trim());
  const overview = trpc.agents.overview.useQuery({ scope });
  const input = useMemo(
    () => ({
      scope,
      ...(query ? { q: query } : {}),
      ...(status === 'to_review' ? { toReview: true } : status !== 'all' ? { status: [status] } : {}),
      ...(runtime !== 'all' ? { runtime: [runtime] } : {}),
      limit: 30,
    }),
    [scope, query, status, runtime],
  );
  const runs = trpc.agents.list.useInfiniteQuery(input, {
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 15_000,
  });
  const items: AgentRunListItem[] = runs.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = Boolean(query) || status !== 'all' || runtime !== 'all';

  return (
    <div className="mt-6 flex flex-col gap-4 md:gap-6">
      {overview.isLoading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Skeleton className="h-[116px]" />
          <Skeleton className="h-[116px]" />
          <Skeleton className="h-[116px]" />
        </div>
      ) : overview.data ? (
        <OverviewTiles data={overview.data} />
      ) : null}

      <Section aria-labelledby="ostatnie-przebiegi">
        <SectionHeader tone="strong" title={<span id="ostatnie-przebiegi">Ostatnie przebiegi</span>} />
        <div className="mt-2 mb-3 flex flex-col gap-2 md:flex-row md:items-center">
          <Input
            icon={Search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Szukaj po problemie…"
            aria-label="Szukaj przebiegów"
            className="md:max-w-[360px]"
          />
          <div className="flex gap-2">
            <Select
              value={status}
              onValueChange={setStatus}
              options={STATUS_OPTIONS}
              aria-label="Status"
              className="min-w-0 flex-1 md:w-[190px] md:flex-none [&>span]:truncate"
            />
            <Select
              value={runtime}
              onValueChange={setRuntime}
              options={RUNTIME_OPTIONS}
              aria-label="Wykonawca"
              className="min-w-0 flex-1 md:w-[230px] md:flex-none [&>span]:truncate"
            />
          </div>
        </div>
        {runs.isLoading ? (
          <ListSkeleton rows={5} />
        ) : runs.isError ? (
          <ErrorState onRetry={() => void runs.refetch()} retrying={runs.isFetching} />
        ) : items.length === 0 ? (
          filtered ? (
            <EmptyState
              icon={Search}
              title="Nic nie pasuje"
              description="Spróbuj innych słów albo zmień filtry."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setQ('');
                    setStatus('all');
                    setRuntime('all');
                  }}
                  className={TOUCH_BUTTON}
                >
                  Wyczyść filtry
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Bot}
              title="Jeszcze nie ma przebiegów"
              description="Zleć Claude z karty zadania, incydentu albo z Copilota — przebieg pojawi się tutaj z dziennikiem, wynikiem i kosztem."
              action={
                access.can('tasks') ? (
                  <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                    <Link to="/zadania">Przejdź do zadań</Link>
                  </Button>
                ) : undefined
              }
            />
          )
        ) : (
          <>
            <RunsList items={items} showCost={access.flag('seesRates')} />
            {runs.hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="secondary"
                  loading={runs.isFetchingNextPage}
                  onClick={() => void runs.fetchNextPage()}
                  className={TOUCH_BUTTON}
                >
                  Pokaż starsze
                </Button>
              </div>
            )}
          </>
        )}
        <p className="mt-5 flex items-start gap-2 text-caption text-fg-muted">
          <Info size={14} strokeWidth={1.75} aria-hidden className="mt-px shrink-0" />
          {AGENTS_SAFETY_NOTE}
        </p>
      </Section>
    </div>
  );
}
